/**
 * Cover selector — swatch design behaviour (wood + woven product templates)
 * ---------------------------------------------------------------------------
 * The cover picker is injected by the masayaco-product-selectors app, so the
 * theme owns only its presentation. product.css restyles the app's cards into
 * a swatch row; this script supplies what CSS cannot express:
 *
 *   1. SELECTED OPTIONS IN THE LABEL — the app prints each option's colour and
 *      price inside its own row. The swatch design hides those and shows the
 *      chosen ones next to the heading instead, matching the "Style: Sealed
 *      Teak" pattern above it. Covers are checkboxes, so more than one can be
 *      added; every selected cover is listed, joined with " + ", in the order
 *      the customer picked them:
 *        "Add a cover: Tan (+$218)"
 *        "Add a cover: Tan (+$218) + Grey (+$218)"
 *
 *   2. ACCESSIBLE NAMES — the swatch design hides the per-row text, which
 *      would otherwise leave each checkbox with no accessible name. Each input
 *      gets an aria-label carrying the colour and price.
 *
 * The app mounts asynchronously and re-renders its list when the parent
 * variant changes, so everything is bound through observers rather than by
 * walking the rows once at load.
 */
(function () {
  'use strict';

  var SELECTOR_ID = 'spa-cover-selector';
  var ROW = '.spa-cover-row';

  function init(root) {
    if (!root || root.dataset.swatchBehaviourBound === 'true') return;
    root.dataset.swatchBehaviourBound = 'true';

    var baseLabel = root.getAttribute('data-section-label') || 'Add a cover:';
    var observer = null;
    /* checkbox values in the order they were ticked, so the label reads in
       the sequence the customer chose rather than in DOM order */
    var pickOrder = [];

    /* "+$215.00" -> "+$215". Only a trailing whole-dollar .00 is dropped, so a
       genuine "+$215.50" keeps its cents. */
    function tidyPrice(text) {
      return (text || '').trim().replace(/\.00(?!\d)/, '');
    }

    function optionText(row) {
      if (!row) return { name: '', price: '' };
      var variantEl = row.querySelector('.spa-cover-variant');
      var priceEl = row.querySelector('.spa-cover-price');
      return {
        name: variantEl ? variantEl.textContent.trim() : '',
        price: priceEl ? tidyPrice(priceEl.textContent) : ''
      };
    }

    function renderLabel() {
      var label = root.querySelector('.spa-section-label');
      if (!label) return;

      var checked = Array.prototype.slice.call(
        root.querySelectorAll(ROW + ' input[type="checkbox"]:checked')
      );

      /* keep pickOrder in step with reality: drop anything no longer ticked,
         then append ticked boxes we haven't seen (e.g. restored by the app
         after a repaint) in DOM order */
      var checkedValues = checked.map(function (input) { return input.value; });
      pickOrder = pickOrder.filter(function (v) { return checkedValues.indexOf(v) !== -1; });
      checkedValues.forEach(function (v) {
        if (pickOrder.indexOf(v) === -1) pickOrder.push(v);
      });

      var pieces = [];
      pickOrder.forEach(function (value) {
        var input = checked.filter(function (i) { return i.value === value; })[0];
        if (!input) return;
        var parts = optionText(input.closest(ROW));
        if (!parts.name && !parts.price) return;
        pieces.push(parts.price ? parts.name + ' (' + parts.price + ')' : parts.name);
      });

      var suffix = pieces.join(' + ');
      var wanted = suffix ? baseLabel + ' ' + suffix : baseLabel;

      /* Bail out when nothing changed. This is what keeps the MutationObserver
         below from looping: textContent always replaces child nodes, so an
         unconditional write would mutate the subtree we observe, which would
         schedule another write, forever. */
      if (label.textContent === wanted) return;

      label.textContent = baseLabel;
      if (suffix) {
        /* textContent + appendChild rather than innerHTML: name and price are
           app-supplied strings and must never be parsed as markup. */
        label.appendChild(document.createTextNode(' '));
        var value = document.createElement('span');
        value.className = 'spa-selected-value';
        value.textContent = suffix;
        label.appendChild(value);
      }
    }

    /* The row's visible text is display:none in this design, so without this
       each checkbox would expose no accessible name at all. */
    function labelInputs() {
      var rows = root.querySelectorAll(ROW);
      Array.prototype.forEach.call(rows, function (row) {
        var input = row.querySelector('input[type="checkbox"]');
        if (!input) return;
        var parts = optionText(row);
        if (!parts.name && !parts.price) return;
        var text = parts.price ? parts.name + ' ' + parts.price : parts.name;
        if (input.getAttribute('aria-label') !== text) {
          input.setAttribute('aria-label', text);
        }
      });
    }

    root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || input.type !== 'checkbox') return;
      /* record the tick so this cover lands last in the label */
      if (input.checked) {
        pickOrder = pickOrder.filter(function (v) { return v !== input.value; });
        pickOrder.push(input.value);
      }
      renderLabel();
    });

    /* Match the swatch size, spacing and ring thickness to the Style/Finish
       tile. A swiper sizes that tile at runtime, so there is no fixed value to
       hard-code — read them and publish them for product.css to consume. */
    function visibleTiles() {
      return Array.prototype.filter.call(
        document.querySelectorAll('.pattern-option'),
        function (t) { return t.getBoundingClientRect().width > 0; }
      );
    }

    /* Copy BOTH the tile size and the spacing between tiles. Neither is a
       fixed CSS value — the swiper derives them from the column width (e.g.
       124px/3px at one width, 70px/5px at another, 62.8px at another) — so
       they are read off the rendered tiles. No rounding: the swiper produces
       fractional sizes, and rounding 62.8 would already be a visible miss. */
    function matchSwatchSize() {
      var tiles = visibleTiles();
      if (!tiles.length) return;
      var a = tiles[0].getBoundingClientRect();
      var width = +a.width.toFixed(2);
      if (width > 20) root.style.setProperty('--spa-cover-size', width + 'px');

      /* Copy the selection ring's stroke width too. The Style tile draws its
         ring as a ::before border (transparent until selected, but the width
         is always set), so read it straight off the tile. */
      var ring = parseFloat(window.getComputedStyle(tiles[0], '::before').borderTopWidth);
      if (ring > 0 && ring < 10) root.style.setProperty('--spa-cover-ring', ring + 'px');

      if (tiles.length > 1) {
        var b = tiles[1].getBoundingClientRect();
        /* only a same-row neighbour tells us the horizontal gap */
        if (Math.abs(b.top - a.top) < 2) {
          var gap = +(b.left - a.right).toFixed(2);
          if (gap >= 0 && gap < 40) root.style.setProperty('--spa-cover-gap', gap + 'px');
        }
      }

      /* Line the first cover up under the first Style tile. The picker's own
         margins (and a negative offset on its wrapper) put that tile a pixel
         or so off the column edge, and the amount changes per breakpoint, so
         measure it: take the first cover's position with the current shift
         removed, and shift the row by the difference. */
      var firstRow = root.querySelector(ROW);
      /* skip while the covers are not laid out (hidden rows report left 0) */
      if (firstRow && firstRow.getBoundingClientRect().width > 0) {
        var current = parseFloat(root.style.getPropertyValue('--spa-cover-offset')) || 0;
        var base = firstRow.getBoundingClientRect().left - current;
        var offset = +(a.left - base).toFixed(2);
        if (Math.abs(offset) < 20 && offset !== current) {
          root.style.setProperty('--spa-cover-offset', offset + 'px');
        }
      }
    }

    var resizeTimer = null;
    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(matchSwatchSize, 150);
    }
    window.addEventListener('resize', onResize);

    /* The swiper finishes sizing its slides after this script has run, and a
       re-layout of the carousel fires no window resize. Watching the tile
       itself catches both — this is what left the covers at 66px while the
       options had settled at 62.8px. */
    var tileObserver = null;
    if ('ResizeObserver' in window) {
      tileObserver = new ResizeObserver(function () { matchSwatchSize(); });
      visibleTiles().slice(0, 2).forEach(function (t) { tileObserver.observe(t); });
    }

    function refresh() {
      matchSwatchSize();
      labelInputs();
      renderLabel();
    }

    /* The app repaints its rows when the parent variant changes, which would
       wipe the appended value and could leave two rows checked. */
    observer = new MutationObserver(function () {
      refresh();
      /* Discard any records our own refresh just produced, so they are not
         delivered as a fresh callback. Combined with the no-op guard in
         renderLabel this makes re-entry impossible. */
      if (observer) observer.takeRecords();
    });
    observer.observe(root, { childList: true, subtree: true });

    /* If the app tears the selector out of the page, stop observing it and
       release the listener rather than holding the detached node alive. */
    var detachWatcher = new MutationObserver(function () {
      if (document.body.contains(root)) return;
      if (observer) observer.disconnect();
      if (tileObserver) tileObserver.disconnect();
      window.removeEventListener('resize', onResize);
      window.clearTimeout(resizeTimer);
      detachWatcher.disconnect();
    });
    detachWatcher.observe(document.body, { childList: true, subtree: true });

    refresh();
  }

  function boot() {
    var existing = document.getElementById(SELECTOR_ID);
    if (existing) {
      init(existing);
      return;
    }

    /* the app block mounts after the theme's own scripts, so watch for it —
       and stop watching as soon as it appears, instead of running on every
       DOM change for the life of the page */
    var pageObserver = new MutationObserver(function () {
      var root = document.getElementById(SELECTOR_ID);
      if (!root) return;
      pageObserver.disconnect();
      init(root);
    });
    pageObserver.observe(document.body, { childList: true, subtree: true });

    /* give up after 30s so a product with no cover block does not leave an
       observer running forever */
    window.setTimeout(function () {
      pageObserver.disconnect();
    }, 30000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
